import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { DatePicker } from "antd";
import { LoadingIcon, TopDropdown } from "../../../components";
import { getGreenhouses } from "../../../utils/api";
import { UserContext } from "../../../context/UserContext";
import { useLocalStorage } from "../../../hooks";

dayjs.extend(isoWeek);

const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  setCurrentMetric,
  setMetricType,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSites,
  currentSite,
  setCurrentSites,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentVariety,
  setCurrentVariety,
  varietyData,
  setVarietyData,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentDate,
  setCurrentDate,
  setMetricData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  disabledDate,
  metricData,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [greenhouseLoading, setGreenhouseLoading] = useState(false);
  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [varietyLabel, setVarietyLabel] = useState("Variety");

  // --------------------------------------
  // Fetch greenhouses from API (first site only)
  // --------------------------------------
  async function fetchGreenhouses(orgId, siteArr, cropId) {
    setGreenhouseLoading(true);
    if (!siteArr?.length) {
      setGreenhouseData([]);
      setGreenhouseLoading(false);
      return;
    }
    const firstSiteId = siteArr[0].id;
    const res = await getGreenhouses(orgId, firstSiteId, cropId);
    const greenhouseList = Array.isArray(res?.data?.data)
      ? res.data.data
      : [];
    const data = greenhouseList.map((gh) => ({
      name: gh.name,
      id: gh.id,
      varieties: gh.varieties || [],
      metadata: gh.metadata || {},
    }));
    setGreenhouseData(data);
    setGreenhouseLoading(false);
  }

  // --------------------------------------
  // Handle the DatePicker
  // --------------------------------------
  const handleDateChange = (date) => {
    if (date) {
      const monday = date.isoWeekday(1);
      setCurrentDate(monday.format("YYYY-MM-DD"));
    } else {
      setCurrentDate(null);
    }
  };

  // --------------------------------------
  // Whenever currentSites or org or crop changes => fetch GH
  // --------------------------------------
  useEffect(() => {
    if (currentSites?.length && currentOrg && currentCrop) {
      fetchGreenhouses(currentOrg, currentSites, currentCrop.id);
    } else {
      setGreenhouseData([]);
    }
  }, [currentSites, currentOrg, currentCrop]);

  // --------------------------------------
  // If siteMetadata has greenhouse_name/variety_name => override
  // --------------------------------------
  useEffect(() => {
    if (siteMetadata && Object.keys(siteMetadata).length) {
      if (siteMetadata.greenhouse_name) {
        setGreenHouseLabel(siteMetadata.greenhouse_name);
      }
      if (siteMetadata.variety_name) {
        setVarietyLabel(siteMetadata.variety_name);
      }
    }
  }, [siteMetadata]);

  // --------------------------------------
  // If we have exactly one site + a crop + selected greenhouses => set metricData
  // --------------------------------------
  useEffect(() => {
    if (
      currentSites?.length === 1 &&
      currentCrop &&
      currentGreenhouse?.length > 0
    ) {
      const singleSite = currentSites[0];
      const meta = singleSite.metadata?.[currentCrop.name] || {};
      setSiteMetadata(meta);
      const metrics = meta.metrics
        ? Object.entries(meta.metrics).map(([type, arr]) => ({
            type,
            metrics: arr,
          }))
        : [];
      setMetricData(metrics);
    } else {
      setMetricData([]);
    }
  }, [currentSites, currentCrop, currentGreenhouse]);

  // --------------------------------------
  // After greenhouseData is fetched => pick default greenhouse from
  //   1) URL param,
  //   2) localStorage (IDs),
  //   3) fallback => all
  // Then set variety data accordingly
  // --------------------------------------
  useEffect(() => {
    if (greenhouseData?.length) {
      const params = new URLSearchParams(searchParams);
      const ghParam = params.get("greenhouses");
      let selectedGHIds = [];

      if (ghParam) {
        selectedGHIds = ghParam.split(",");
      } else if (!settingDefaults.greenhouses) {
        const defGH = defaultDropdown?.[currentOrg]?.greenhouses;
        if (defGH?.length) {
          selectedGHIds = defGH;
        } else {
          selectedGHIds = greenhouseData.map((g) => g.id);
        }
        setSettingDefaults((prev) => ({ ...prev, greenhouses: true }));
      } else {
        selectedGHIds = greenhouseData.map((g) => g.id);
      }

      let selectedGH = greenhouseData.filter((g) =>
        selectedGHIds.includes(g.id),
      );
      if (!selectedGH.length) {
        selectedGH = greenhouseData;
      }

      setCurrentGreenhouse(selectedGH);

      // Set variety data from these GH
      const combinedVarieties = selectedGH.flatMap((gh) => gh.varieties || []);
      const uniqueVarieties = Array.from(
        new Map(combinedVarieties.map((p) => [p.id, p])).values(),
      );
      setVarietyData(uniqueVarieties);
    } else {
      setCurrentGreenhouse([]);
      setVarietyData([]);
    }
  }, [greenhouseData]);

  // --------------------------------------
  // Whenever currentGreenhouse changes => store in param + localStorage
  // Then recalc variety data
  // --------------------------------------
  useEffect(() => {
    if (currentGreenhouse?.length) {
      const params = new URLSearchParams(searchParams);
      const ghIds = currentGreenhouse.map((g) => g.id).join(",");
      params.set("greenhouses", ghIds);
      setSearchParams(params);

      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouses: currentGreenhouse.map((gh) => gh.id),
        },
      });

      // Recalc varieties from these GH
      const combinedVarieties = currentGreenhouse.flatMap(
        (g) => g.varieties || [],
      );
      const uniqueVarieties = Array.from(
        new Map(combinedVarieties.map((p) => [p.id, p])).values(),
      );
      handleVarietyDefaultSelection(uniqueVarieties);
    } else {
      setVarietyData([]);
      setCurrentVariety([]);
    }
  }, [currentGreenhouse]);

  // --------------------------------------
  // handle variety default selection => URL param, localStorage, or fallback
  // --------------------------------------
  function handleVarietyDefaultSelection(varieties) {
    setVarietyData(varieties);

    if (!varieties?.length) {
      setCurrentVariety([]);
      return;
    }

    const params = new URLSearchParams(searchParams);
    const varietyParam = params.get("variety");
    let selectedVarietyIds = [];

    if (varietyParam) {
      selectedVarietyIds = varietyParam.split(",");
    } else if (!settingDefaults.variety) {
      const defVar = defaultDropdown?.[currentOrg]?.varieties;
      if (defVar?.length) {
        selectedVarietyIds = defVar;
      } else {
        selectedVarietyIds = varieties.map((v) => v.id);
      }
      setSettingDefaults((prev) => ({ ...prev, variety: true }));
    } else {
      selectedVarietyIds = varieties.map((v) => v.id);
    }

    let selectedVarieties = varieties.filter((v) =>
      selectedVarietyIds.includes(v.id),
    );
    if (!selectedVarieties.length) {
      selectedVarieties = varieties;
    }
    setCurrentVariety(selectedVarieties);
  }

  // --------------------------------------
  // Whenever currentVariety changes => store IDs in param + localStorage
  // --------------------------------------
  useEffect(() => {
    if (currentVariety?.length) {
      const params = new URLSearchParams(searchParams);
      const varietyIds = currentVariety.map((p) => p.id).join(",");
      params.set("variety", varietyIds);
      setSearchParams(params);

      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          varieties: currentVariety.map((v) => v.id), // store IDs
        },
      });
    }
  }, [currentVariety]);

  // --------------------------------------
  // If exactly one site + crop => set default metrics
  // --------------------------------------
  useEffect(() => {
    if (currentSites?.length === 1 && currentCrop) {
      const singleSite = currentSites[0];
      const defMetrics =
        singleSite.metadata?.[currentCrop.name]?.default_metrics || [];
      setMetricType(defMetrics[0] || null);
      setCurrentMetric(defMetrics[1] || null);
    } else {
      setMetricType(null);
      setCurrentMetric(null);
    }
  }, [currentSites, currentCrop]);

  // --------------------------------------
  // Set overall dropdown loading
  // --------------------------------------
  useEffect(() => {
    setDropdownLoading(
      orgLoading || siteLoading || cropLoading || greenhouseLoading,
    );
  }, [
    orgLoading,
    siteLoading,
    cropLoading,
    greenhouseLoading,
    setDropdownLoading,
  ]);

  // --------------------------------------
  // handleMultiSiteChange => user picks multiple sites by ID
  // --------------------------------------
  function handleMultiSiteChange(siteIds) {
    const selectedSiteObjs = siteData.filter((s) => siteIds.includes(s.id));
    setCurrentSites(selectedSiteObjs);

    // store in query param
    const params = new URLSearchParams(searchParams);
    const joined = selectedSiteObjs.map((s) => s.id).join(",");
    params.set("sites", joined);
    setSearchParams(params);

    let orgObj = defaultDropdown?.[currentOrg] || {};
    setDefaultDropdown({
      ...defaultDropdown,
      [currentOrg]: {
        ...orgObj,
        sites: selectedSiteObjs.map((s) => s.id), // store site IDs
      },
    });
  }

  // --------------------------------------
  // On mount or data load => see if there's a "sites" param
  // or localStorage default. Otherwise fallback.
  // --------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const siteParam = params.get("sites");
    if (siteParam) {
      const sIds = siteParam.split(",");
      const matched = siteData?.filter((sd) => sIds.includes(sd.id)) || [];
      if (matched.length) {
        setCurrentSites(matched);
        return;
      }
    } else if (!settingDefaults.site) {
      const defSites = defaultDropdown?.[currentOrg]?.sites;
      if (defSites?.length) {
        const matched =
          siteData?.filter((sd) => defSites.includes(sd.id)) || [];
        setCurrentSites(matched);
      }
      setSettingDefaults((prev) => ({ ...prev, site: true }));
    } else {
      if (currentSite) {
        setCurrentSites([currentSite]);
      }
    }
  }, [siteData]);

  // --------------------------------------
  // Return the component UI
  // --------------------------------------
  return (
    <>
      {orgLoading || siteLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns !font-MuseoSans">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[200px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg} // ID
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(a, b) =>
                (a?.label ?? "")
                  .toLowerCase()
                  .localeCompare((b?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData?.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          <TopDropdown
            label="Select Site(s):"
            mode="multiple"
            maxTagCount={2}
            val={currentSites?.map((s) => s.id) ?? []}
            classString="flex-1 min-w-[200px] sm:mb-2"
            placeholder="Select Site(s)"
            changeCallback={handleMultiSiteChange}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[200px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={cropData?.map((c) => ({
              value: c.id,
              label: c.displayName,
            }))}
          />

          <TopDropdown
            label={`Select ${greenHouseLabel}:`}
            mode="multiple"
            maxTagCount={2}
            val={currentGreenhouse?.map((g) => g.id) ?? []}
            placeholder={`Select ${greenHouseLabel}`}
            classString="flex-1 min-w-[200px] sm:mb-2"
            changeCallback={(ghIds) => {
              const selectedGHs = greenhouseData?.filter((gh) =>
                ghIds.includes(gh.id),
              );
              setCurrentGreenhouse(selectedGHs || []);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={greenhouseData?.map((gh) => ({
              value: gh.id,
              label: gh.name,
            }))}
          />

          <TopDropdown
            label={`Select ${varietyLabel}:`}
            mode="multiple"
            maxTagCount={2}
            val={currentVariety?.map((v) => v.id) ?? []}
            placeholder={`Select ${varietyLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(varietyIds) => {
              const selectedVarieties = varietyData?.filter((v) =>
                varietyIds.includes(v.id),
              );
              setCurrentVariety(selectedVarieties || []);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={varietyData?.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />

          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Week:
            </div>
            <DatePicker
              picker="week"
              value={currentDate ? dayjs(currentDate) : null}
              className="flex-1 min-w-[200px] sm:mb-2"
              disabledDate={disabledDate}
              onChange={handleDateChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Dropdowns;
